import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar4 from "../components/Navbar4";
import { getRevisionStatus } from "../services/LLMCalls";
import ProcessWarning from "../components/ProcessWarning";
import ProcessingTime from '../components/ProcessingTime';

const PromptStatus3 = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    projectId,
    viewId,
    versionId,
    isProcessing,
    error: initialError
  } = location.state || {};

  const [status, setStatus] = useState("processing");
  const [error, setError] = useState(initialError || null);
  const [showModal, setShowModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  
  // Use a ref for attempts counter instead of state
  const attemptsRef = useRef(0);
  
  // Use a ref for timestamp to ensure it never changes
  const startTimestampRef = useRef(Date.now());
  const startTimestamp = startTimestampRef.current;
  
  // Status ref to avoid stale closures
  const statusRef = useRef(status);
  statusRef.current = status;
  
  const MAX_ATTEMPTS = 30; // same as PromptStatus2

  // Intercept navigation when status is processing
  const handleNavigate = useCallback(
    (to, options) => {
      if (statusRef.current === "processing") {
        setShowModal(true);
        setPendingNavigation(() => () => navigate(to, options));
      } else {
        navigate(to, options);
      }
    },
    [navigate]
  );

  // Listen for browser back/forward navigation
  useEffect(() => {
    if (status !== "processing") return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [status]);

  useEffect(() => {
    if (!projectId || !viewId || !versionId) {
      setError("Missing required parameters");
      return;
    }

    if (!isProcessing && !versionId) {
      setError("Invalid status check request");
      return;
    }

    // Flag to prevent state updates after component unmount
    let isMounted = true;
    let currentStatus = "processing";
    
    console.log(`[PromptStatus3] Starting polling for viewId: ${viewId}, versionId: ${versionId}`);

    const checkStatus = async () => {
      try {
        if (!isMounted) return;
        console.log(`[PromptStatus3] Checking status for viewId: ${viewId}, versionId: ${versionId}`);
        
        const result = await getRevisionStatus(viewId, versionId);

        // Only update state if component is still mounted
        if (!isMounted) return;
        
        if (result.revision_status) {
          currentStatus = result.revision_status;
          console.log(`[PromptStatus3] Received status: ${currentStatus}`);
          
          if (currentStatus !== statusRef.current) {
            setStatus(currentStatus);
          }

          if (currentStatus === "error") {
            setError(result.error_message || "An error occurred while processing your request.");
          } else if (currentStatus === "complete") {
            console.log(`[PromptStatus3] Status complete, navigating to view-details`);
            // Navigate directly without changing status first to avoid flickering
            navigate("/view-details", {
              state: {
                projectId,
                viewId
              },
              replace: true
            });
            return; // Exit polling on completion
          }
        }
      } catch (err) {
        console.error(`[PromptStatus3] Error checking status:`, err);
        if (!isMounted) return;
        
        // Use the ref for attempts counter
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setError("Request timed out. Please try again.");
        }
      }
    };

    // Initial check
    checkStatus();
    
    // Setup polling interval
    const interval = setInterval(checkStatus, 2000);
    
    // Cleanup function
    return () => {
      console.log(`[PromptStatus3] Cleaning up polling for viewId: ${viewId}, versionId: ${versionId}`);
      isMounted = false;
      clearInterval(interval);
    };
  }, [projectId, viewId, versionId, navigate, isProcessing, MAX_ATTEMPTS]);

  // Pass custom back handler to Navbar4
  const handleBack = useCallback(() => {
    handleNavigate("/view-details", { state: { projectId, viewId } });
  }, [handleNavigate, projectId, viewId]);

  // Modal handlers
  const handleModalCancel = () => {
    setShowModal(false);
    setPendingNavigation(null);
  };
  const handleModalContinue = () => {
    setShowModal(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };

  const ErrorDisplay = () => (
    <div className="text-center p-4">
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
      {/* Show Purchase Access Pass button for subscription errors */}
      {(location.state?.isSubscriptionError || (error && (
        error.includes("trial") ||
        error.includes("expired") ||
        error.includes("Purchase") ||
        error.includes("access pass")
      ))) ? (
        <button
          onClick={() => navigate('/payment')}
          className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 mr-2"
        >
          Purchase Access Pass
        </button>
      ) : null}
      <button
        onClick={() => navigate("/view-details", { state: { projectId, viewId }})}
        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-700"
      >
        Back to View Details
      </button>
    </div>
  );

  const LoadingDisplay = () => (
    <div className="text-center p-4">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900 mx-auto mb-4"></div>
      <p className="text-xl mb-2">
        {status === 'processing' && 'Processing your revision...'}
        {status === 'analyzing' && 'Analyzing your requested changes...'}
        {status === 'generating' && 'Generating your revised HTML...'}
      </p>
      <p className="text-lg text-gray-600">This may take a few moments</p>
      
      <ProcessingTime 
        promptType="revision" 
        status={status} 
        startTimestamp={startTimestamp} 
      />
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar4
        title="Processing Revision"
        backPath="/view-details"
        projectId={projectId}
        onBack={handleBack}
      />
      <div className="flex-grow flex items-center justify-center">
        {error ? <ErrorDisplay /> : <LoadingDisplay />}
      </div>
      <ProcessWarning
        open={showModal}
        onCancel={handleModalCancel}
        onContinue={handleModalContinue}
      />
    </div>
  );
};

export default PromptStatus3;